import { Store, StoreSyncInfo } from "../../../types";
import { createLog, editStoreSync, enableStore, syncCall } from "../../../lib/admin";
import { useState, useEffect, useCallback } from "react";
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Select from '@mui/material/Select';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { MenuItem } from "@mui/material";
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControl from '@mui/material/FormControl';
import Tooltip from '@mui/material/Tooltip';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from "dayjs";
import DateIcon from "../../../assets/DateIcon";
import formatDateToLocal from "../../../utils/formatDateToLocal";
import { useAuth } from "../../../utils/authorization";
import { useHasAllStoresAccess, useHasScope } from "../../../utils/Permission";
import { SyncNow } from "../../../assets/SyncNow";
import { RepeatSyncEvery } from "../../../assets/RepeatSyncEvery";
import SyncStatusModal from "./SyncStatusModal";
import StickySaveBar from "../../global/StickySaveBar";
import Notification from "../../global/Notification";
import syncIcon from "../../../assets/management/sync.svg";

type Props =
    {
        store: Store,
        hasUnsavedChanges: boolean,
        setHasUnsavedChanges: (flag: boolean) => void,
        hasBeenUpdated: boolean,
        setHasBeenUpdated: (flag: boolean) => void
    }



type AutoSyncValues = {
    isLocked: boolean,
    startDate: Date | null,
    executionMode: string,
    periodMinutes: number,
}

export default function StoreSyncForm({ store, setHasUnsavedChanges, setHasBeenUpdated }: Props) {
    const { user } = useAuth();
    const hasAllStoresAccess = useHasAllStoresAccess();
    const utcTimeZone = user?.localTimezone;
    const isLoggedIn = !!user;
    const getInitialDate = (syncStartTime: unknown, useUTC: boolean): Date | null => {
        if (!syncStartTime) return null;
        return useUTC ? dayjs().add(3, 'hour').toDate() : new Date();
    };
    const initialDate = getInitialDate(store.syncStartTime, utcTimeZone as boolean);

    const [baseline, setBaseline] = useState<AutoSyncValues>(() => ({
        isLocked: !store.automaticSyncEnabled,
        startDate: store.syncStartTime == null
            ? null
            : !utcTimeZone
                ? new Date(formatDateToLocal(store.syncStartTime))
                : new Date(store.syncStartTime),
        executionMode: store.executionMode != null ? store.executionMode == 0 ? "one-time" : "permanent" : "one-time",
        periodMinutes: store.periodMinutes != null ? store.periodMinutes : 30,
    }));
    const [drafts, setDrafts] = useState<AutoSyncValues>(baseline);
    const [syncState, setSyncState] = useState<number>(store.syncState);
    const [enabled, setEnabled] = useState(store.enabled);
    const hasSyncUnlimited = useHasScope('stores:sync:unlimited');
    const hasUpdateStoreSync = useHasScope('update:storesyncsettings');
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [notification, setNotification] = useState<{ message: string | null; type: 'success' | 'error' }>({
        message: null,
        type: 'success',
    });

    const dismissSaveError = useCallback(() => setSaveError(null), []);
    const closeNotification = useCallback(() => setNotification({ message: null, type: 'success' }), []);

    const { isLocked, startDate, executionMode: selectedSyncType, periodMinutes: repeatSyncValue } = drafts;
    const hasChanges =
        drafts.isLocked !== baseline.isLocked ||
        (!drafts.isLocked && (
            drafts.startDate?.getTime() !== baseline.startDate?.getTime() ||
            drafts.executionMode !== baseline.executionMode ||
            drafts.periodMinutes !== baseline.periodMinutes
        ));

    useEffect(() => {
        setHasUnsavedChanges(hasChanges);
    }, [hasChanges, setHasUnsavedChanges]);

    const checkStoreTime: (store: Store) => boolean = (store: Store) => {
        var now = new Date();
        var now_utc = new Date(now.toUTCString().slice(0, -5));
        var lastUpdate = new Date(store.lastUpdate);

        if (getMinDiff(now_utc, lastUpdate) < 10) {
            return false;
        }
        else return true;
    }

    const getMinDiff: (startDate: any, endDate: any) => number = (startDate: any, endDate: any) => {
        const msInMinute = 60 * 1000;
        return Math.floor(
            Math.abs(endDate - startDate) / msInMinute
        );
    }

    const syncStore = async () => {
        if (!checkStoreTime(store) && !hasSyncUnlimited) {
            setNotification({ message: 'Last sync was less than 10 minutes ago, try again later.', type: 'error' });
            return;
        }

        try {
            const response = await syncCall(store.id, { email: user?.email });
            if (response === '') {
                setSyncState(1);
                setNotification({ message: "Synchronization started.", type: 'success' });
            }
            else {
                setNotification({
                    message: typeof response === "string" ? response : "An error occurred. Please try again later.",
                    type: 'error',
                });
            }
        } catch (error) {
            console.error("Failed to start synchronization:", error);
            setNotification({ message: "An error occurred. Please try again later.", type: 'error' });
        }
    };

    const minutesSync = [
        { value: "10", label: "10 minutes" },
        { value: "15", label: "15 minutes" },
        { value: "20", label: "20 minutes" },
        { value: "30", label: "30 minutes" },
        { value: "60", label: "1 hour" },
        { value: "120", label: "2 hours" },
        { value: "180", label: "3 hours" },
        { value: "360", label: "6 hours" },
        { value: "720", label: "12 hours" },
        { value: "1080", label: "18 hours" },
        { value: "1440", label: "1 day" },
        { value: "4320", label: "3 days" },
        { value: "10080", label: "7 days" },
        { value: "43200", label: "30 days" }
    ];

    const labelByValue = new Map(minutesSync.map(o => [o.value, o.label]));

    const repeatMinuteItems = minutesSync.map((option) => (
        <MenuItem key={option.value} value={option.value}>
            <em>{option.label}</em>
        </MenuItem>
    ));

    const handleLockToggle = () => {
        setDrafts((current) => ({ ...current, isLocked: !current.isLocked }));
    };

    const handleDateChange = (newDate: Date | null) => {
        setDrafts((current) => ({ ...current, startDate: newDate }));
    };

    const handleExecutionMode = (value: string) => {
        setDrafts((current) => ({ ...current, executionMode: value }));
    };

    const handleRepeatSyncValue = (value: number) => {
        setDrafts((current) => ({ ...current, periodMinutes: value }));
    }

    const handleCancelChanges = () => {
        setDrafts(baseline);
        setSaveError(null);
    };

    const handleSave = async () => {
        if (!hasUpdateStoreSync) {
            setSaveError("You do not have permission to update this store.");
            return;
        }

        let storeChanges: StoreSyncInfo;
        let saved: AutoSyncValues;

        if (isLocked) {
            storeChanges = {
                id: store.id,
                automaticSyncEnabled: false,
                executionMode: store.executionMode,
                syncStartTime: store.syncStartTime,
                periodMinutes: store.periodMinutes,
                nextSync: null
            };
            saved = { ...baseline, isLocked: true };
        } else {
            if (startDate === null || isNaN(startDate.getTime())) {
                setSaveError("Select a valid date.");
                return;
            }

            let serverTimezone = "";

            if (!utcTimeZone) {
                if (startDate < new Date()) {
                    setSaveError("Please select a future date.");
                    return;
                }
                serverTimezone = startDate.toISOString();
            } else {
                const currentUTC = new Date().toISOString();

                const year = startDate.getFullYear();
                const month = String(startDate.getMonth() + 1).padStart(2, '0');
                const day = String(startDate.getDate()).padStart(2, '0');
                const hours = String(startDate.getHours()).padStart(2, '0');
                const minutes = String(startDate.getMinutes()).padStart(2, '0');
                const seconds = String(startDate.getSeconds()).padStart(2, '0');

                const constructedTime = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.000Z`;

                if (constructedTime < currentUTC) {
                    setSaveError("Please select a future date.");
                    return;
                }
                serverTimezone = constructedTime;
            }

            if (!selectedSyncType) {
                setSaveError("Select a type of synchronization setting.");
                return;
            }

            storeChanges = {
                id: store.id,
                automaticSyncEnabled: true,
                executionMode: selectedSyncType === "one-time" ? 0 : 1,
                syncStartTime: serverTimezone,
                periodMinutes: repeatSyncValue,
                nextSync: serverTimezone
            };
            saved = drafts;
        }

        setSaveError(null);
        setSaving(true);
        try {
            const response = await editStoreSync(storeChanges);

            if (response) {
                createLog({
                    storeId: store.id,
                    entityType: 'sync',
                    section: 'syncSettings',
                    action: 'save',
                    lastValue: baseline,
                    newValue: saved,
                });
                setDrafts(saved);
                setBaseline(saved);
                setNotification({ message: "Changes saved successfully.", type: 'success' });
                setHasBeenUpdated(true);
            } else {
                setSaveError("An error occurred. Please try again later.");
            }
        } catch (error) {
            console.error("Error saving sync settings:", error);
            setSaveError("An error occurred. Please try again later.");
        } finally {
            setSaving(false);
        }
    };

    const handleToggleClick = () => {
        setShowStatusModal(true);
    };

    const confirmToggle = async (reason: string) => {
        setIsProcessing(true);
        const newEnabledState = !enabled;
        try {
            await enableStore(store.id, newEnabledState, reason);
            setEnabled(newEnabledState);
        } catch (error) {
            console.error("Failed to update store status:", error);
            setNotification({ message: "An error occurred. Please try again later.", type: 'error' });
        } finally {
            setIsProcessing(false);
            setShowStatusModal(false);
        }
    };

    return (
        <div className="sync-management syncPanel">
            <div className="sync-header">
                <p>Shopify Stores/{store.friendlyName}</p>
                <h1><img src={syncIcon} alt="sync store" />Sync Settings</h1>
            </div>
            <Divider className="divider" />
            <span className="sync-automatic-data">Automatic sync is {enabled ? "enabled" : "disabled"}</span>
            <div className="SyncTopArea">
                <div className="SyncTopArea__container grid">
                    <div className="SyncTopArea__grid">
                        <div className="SyncTopArea__leftColumn">
                            <div className="SyncTopArea__fistRow">
                                <div className="SyncTopArea__iconArea">
                                    <div className="inconSync">
                                        <img src={syncIcon} alt="" />
                                    </div>
                                    <div className="syncState">
                                        <div className="column dates">
                                            <div className="field">
                                                <p className="field-label SyncState">Sync State</p>
                                                <p className="sync-state">
                                                    {
                                                        store.syncState === 0
                                                            ? " Idle"
                                                            : store.syncState === 1 || store.syncV2IsRunning === 1
                                                                ? " Running"
                                                                : store.syncState === 3
                                                                    ? " Error"
                                                                    : " Unknown State"
                                                    }
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                </div>
                                <div className="SyncTopArea__lineArea">
                                    <div className="next">
                                        <div className="field">
                                            <p className="field-label nextSync">Next <span>Sync</span></p>
                                            <p className="date">
                                                {!utcTimeZone
                                                    ? (store.nextSync
                                                        ? `${formatDateToLocal(store.nextSync).replace(/T/, ' ').replace(/\..+/, '')} (Local)`
                                                        : 'Not Available (Local)'
                                                    )
                                                    : (store.nextSync
                                                        ? `${store.nextSync.replace(/T/, ' ').replace(/\..+/, '')} (UTC)`
                                                        : 'Not Available (UTC)'
                                                    )
                                                }
                                            </p>
                                        </div>

                                    </div>
                                </div>
                                <div className="SyncTopArea__lineArea">
                                    <div className="inconSync dateicon">
                                        <DateIcon></DateIcon>
                                    </div>
                                    <div className="last">
                                        <div className="column">
                                            <div className="field">
                                                <p className="field-label">Last catalog <span>sync execution</span></p>
                                                <p className="date">
                                                    {!utcTimeZone
                                                        ? (store.lastUpdate
                                                            ? `${formatDateToLocal(store.lastUpdate).replace(/T/, ' ').replace(/\..+/, '')} (Local)`
                                                            : 'Not Available (Local)'
                                                        )
                                                        : (store.lastUpdate
                                                            ? `${store.lastUpdate.replace(/T/, ' ').replace(/\..+/, '')} (UTC)`
                                                            : 'Not Available (UTC)'
                                                        )
                                                    }
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                </div>

                            </div>
                        </div>
                        {isLoggedIn && hasAllStoresAccess && (
                            <div className="SyncTopArea__rightColumn">
                                <div className="SyncTopArea__actions-row">
                                    <div className="card-enable-store">
                                        <label>
                                            Disabled
                                            <input
                                                type="checkbox"
                                                checked={enabled}
                                                onChange={handleToggleClick}
                                            />
                                            Enabled
                                        </label>
                                    </div>

                                    <button disabled={!enabled} type="button" onClick={() => syncStore()} className={!enabled ? "disabled btn SyncNowBtn" : "btn SyncNowBtn"}><SyncNow color={enabled ? "#ffffff" : "#ABBDD6"} /> Sync Now</button>
                                </div>
                            </div>
                        )}
                        <SyncStatusModal
                            open={showStatusModal}
                            onClose={() => setShowStatusModal(false)}
                            onConfirm={confirmToggle}
                            isEnabling={!enabled}
                            isProcessing={isProcessing}
                        />
                    </div>
                </div>
            </div>
            {
                !isLoggedIn
                    ?
                    <></>
                    :
                    <>
                        {hasAllStoresAccess && (
                            <>
                                <div className={!enabled ? "SyncBottomArea locked-form" : "SyncBottomArea"}>
                                    <div className="SyncBottomArea__container">
                                        <div className={(!enabled || !hasUpdateStoreSync) ? "locked-form" : "form-container"}>
                                            <div className="SyncBottomArea__topArea">
                                                <p className="form-title">Automatic Sync</p>
                                                {
                                                    !isLoggedIn
                                                        ?
                                                        <></>
                                                        :
                                                        <div className="SyncTopArea__btnArea">
                                                            <div className="lock-check-wrapper">
                                                                <div className="lock-check">
                                                                    <div className="lock">
                                                                        {
                                                                            isLocked ?
                                                                                <LockIcon></LockIcon>
                                                                                :
                                                                                <LockOpenIcon></LockOpenIcon>
                                                                        }
                                                                    </div>
                                                                    <Tooltip title="Enabling this option will run the automatic sync configuration.">
                                                                        <FormControlLabel className="enableAutomatic"
                                                                            control={<Switch checked={!isLocked} onChange={handleLockToggle} />}
                                                                            label=''
                                                                            labelPlacement="start" />
                                                                    </Tooltip>
                                                                </div>
                                                            </div>
                                                        </div>
                                                }
                                            </div>

                                            <div className="SyncBottomArea__firstRow">

                                                <div className="repeat startRow">
                                                    <div className={isLocked ? 'locked-form field' : 'field'}>
                                                        <p className="field-label">Start</p>
                                                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                            <DateTimePicker
                                                                value={startDate}
                                                                onChange={handleDateChange}
                                                                className={isLocked ? 'locked-form' : ''}
                                                                minDateTime={initialDate || new Date()}
                                                                disablePast
                                                                desktopModeMediaQuery="@media (min-width:0px)"
                                                            />
                                                        </LocalizationProvider>
                                                    </div>
                                                </div>

                                                <div className='mode' >
                                                    <div className={isLocked ? 'locked-form field' : 'field'}>
                                                        <p className="field-label">Execution mode</p>

                                                        <FormControl>
                                                            <RadioGroup
                                                                row
                                                                aria-labelledby="demo-row-radio-buttons-group-label"
                                                                name="row-radio-buttons-group"
                                                                value={selectedSyncType}
                                                                onChange={(event) => handleExecutionMode(event.target.value)}
                                                                className={isLocked ? 'locked-form' : ''}
                                                            >
                                                                <div className="executionMode">
                                                                    <div className="field-form executionP"><FormControlLabel value="one-time" control={<Radio />} label="One Time" /></div>
                                                                    <div className="field-form executionP"><FormControlLabel value="permanent" control={<Radio />} label="Permanent" /></div>
                                                                </div>
                                                            </RadioGroup>
                                                        </FormControl>

                                                    </div>
                                                </div>
                                            </div>

                                            <div className="SyncBottomArea__secondRow">
                                                <div className="repeat">
                                                    <div className="column">

                                                        <div className={isLocked ? 'locked-form field' : 'field'}>
                                                            <p className="field-label">Repeat Sync Every</p>
                                                            <Select
                                                                value={String(repeatSyncValue)}
                                                                variant="outlined"
                                                                onChange={(event) => handleRepeatSyncValue(Number(event.target.value))}
                                                                className={isLocked ? 'locked-form' : ''}
                                                                displayEmpty
                                                                sx={{
                                                                    '& .MuiSelect-select': {
                                                                        backgroundColor: 'transparent !important',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: 1,
                                                                        pl: 0,
                                                                    },
                                                                    '& .MuiOutlinedInput-input': { pl: 0 },
                                                                    '& .MuiSelect-select:focus': { backgroundColor: 'transparent' },
                                                                }}
                                                                renderValue={(value) => (
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                        <RepeatSyncEvery color="#5493DA" />
                                                                        <span>{labelByValue.get(String(value)) ?? 'Select interval'}</span>
                                                                    </Box>
                                                                )}
                                                            >
                                                                {repeatMinuteItems}
                                                            </Select>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </>
            }
            <StickySaveBar
                visible={hasChanges}
                saving={saving}
                onSave={handleSave}
                onCancel={handleCancelChanges}
                alignWithSelector=".leftVertical"
                errorMessage={saveError ?? undefined}
                onErrorDismiss={dismissSaveError}
            />
            <Notification message={notification.message} type={notification.type} onClose={closeNotification} />
        </div >
    );
}